/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1715731200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "snowflake",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Snowflake credential setup",
          "depth": 0,
          "id": "snowflake-credential-setup"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Connection timeout",
          "depth": 1,
          "id": "connection-timeout"
        }, {
          "value": "Network error: Could not reach Snowflake",
          "depth": 1,
          "id": "network-error-could-not-reach-snowflake"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      pre: "pre",
      span: "span",
      strong: "strong",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet, SyncLogsSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    if (!SyncLogsSnippet) _missingMdxReference("SyncLogsSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in your Snowflake warehouse and push it to downstream destinations."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can connect Snowflake to Hightouch using ", _jsx(_components.a, {
            href: "https://hightouch.com/blog/hightouch-snowflake-partner-connect",
            children: "Snowflake Partner Connect"
          }), " to bypass the setup steps outlined below. You can learn more about this in ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/ecosystem-partner-connect",
            children: "Snowflake's documentation"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Connecting Hightouch to Snowflake requires some setup in both platforms. It's recommended to set up a service account with the correct permissions in Snowflake before configuring the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "snowflake-credential-setup",
        children: "Snowflake credential setup"
      }), "\n", _jsx(_components.p, {
        children: "To allow Hightouch access to Snowflake, it's best to create a user specifically provisioned with access to the required tables and schemas. You can also use a personal Snowflake login for your credentials, as long as it has the correct permissions."
      }), "\n", _jsx(_components.p, {
        children: "You can use the following SQL template to create a service account with the necessary roles and permissions with some considerations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "This snippet provides an example of creating a service account; you may need to alter it depending on your Snowflake implementation details."
        }), "\n", _jsxs(_components.li, {
          children: ["If you want Hightouch to access multiple databases, run the snippet multiple times, changing ", _jsx(_components.code, {
            children: "ht_database"
          }), " each time."]
        }), "\n", _jsx(_components.li, {
          children: "If you want Hightouch to have read-only access, omit the last line of the query."
        }), "\n", _jsx(_components.li, {
          children: "The snippet includes lines for creating a new warehouse and database for Hightouch to use; if you already have databases and warehouses you intend to use, omit these lines"
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Edit the following variables"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_username", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'HIGHTOUCH_USER'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_password", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<a secure password>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_first_name", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'Hightouch'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_last_name", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'User'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_warehouse", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<warehouse>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_database", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<database>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_namespace", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'<database.schema>'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_default_role", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'HIGHTOUCH_ROLE'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ht_comment", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "'Used for Hightouch integrations'"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Set role for grants"
          }), "\nUSE ROLE ACCOUNTADMIN;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Create a role for Hightouch"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ROLE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_default_role)\nCOMMENT ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " $ht_comment;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Only if you want to create a new warehouse for Hightouch to use"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " WAREHOUSE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_default_warehouse);\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Create Hightouch's user"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_username)\nPASSWORD", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_password\nFIRST_NAME", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_first_name\nLAST_NAME", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_last_name\nDEFAULT_WAREHOUSE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_warehouse\nDEFAULT_NAMESPACE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_namespace\nDEFAULT_ROLE", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_default_role\nCOMMENT", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), "$ht_comment;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Grant permissions to the role"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE identifier($ht_default_role) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE SYSADMIN;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " WAREHOUSE identifier($ht_default_warehouse) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ROLE identifier($ht_default_role) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " identifier($ht_username);\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Only if you want to create a new database for Hightouch to use"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " DATABASE IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " identifier($ht_database);\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " SCHEMAS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " FUTURE TABLES ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " FUTURE VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " DATABASE identifier($ht_database) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Grant write access for the Lightning Sync Engine (faster syncs) & Sync Logs - Optional"
          }), "\nUSE identifier($ht_database);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " OWNERSHIP ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " OWNERSHIP ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you are using ", _jsx(_components.a, {
          href: "https://gitlab.com/gitlab-data/permifrost",
          children: "Permifrost"
        }), ", you can configure the role and user as follows:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "roles:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "ht_readonly_role:"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "warehouses:"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "<warehouse>"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "privileges:"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "databases:"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "read:"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "<database>"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schemas:"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "read:"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "<database>.*"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tables:"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "read:"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "<database>.*.*"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "users:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "ht_user:"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "can_login:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "yes"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "member_of:"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "ht_readonly_role"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Check out the Hightouch ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/permifrost-orb",
          children: "CircleCI orb"
        }), " for an Orb template to run Permifrost."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you opt to use ", _jsx(_components.a, {
            href: "#choose-your-sync-engine",
            children: "the Lightning engine"
          }), ", you must grant ", _jsx(_components.a, {
            href: "#lightning-engine-setup",
            children: "additional permissions"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Once you've created a Snowflake service account, you're ready to set up the connection in Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsx(WhitelistDetails, {
        target: "Snowflake account",
        privatelink: {
          anchor: "snowflake",
          supportedIntegrations: ["aws-privatelink"]
        }
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Snowflake"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account identifier"
          }), ": You can find this identifier at the beginning of your Snowflake URL, for example, (", _jsx(_components.code, {
            children: "https://ACCOUNT_IDENTIFIER.snowflakecomputing.com"
          }), "). Only enter the ", _jsx(_components.code, {
            children: "ACCOUNT_IDENTIFIER"
          }), " and not the full URL."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warehouse"
          }), ": This specifies the warehouse to use when Hightouch executes queries in Snowflake."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in Snowflake."]
        }), "\n"]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Account identifier"
          }), " format may differ based on Snowflake account age."]
        }), _jsxs(_components.p, {
          children: ["For example, older Snowflake accounts often have identifiers that look like ", _jsx(_components.code, {
            children: "ACCOUNT_LOCATOR.CLOUD_REGION_ID.CLOUD"
          }), ", whereas newer Snowflake accounts have identifiers that look like ", _jsx(_components.code, {
            children: "ORGNAME-ACCOUNT_NAME"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["For more details, visit ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/admin-account-identifier.html",
            children: "Snowflake's account identifier docs"
          }), "."]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Snowflake"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Snowflake"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsx(_components.p, {
        children: "To set up the Lightning engine, ensure that the service account you previously created has the appropriate permissions."
      }), "\n", _jsxs(_components.p, {
        children: ["Specifically, append these commands to the ", _jsx(_components.a, {
          href: "#snowflake-credential-setup",
          children: "service account creation"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " OWNERSHIP ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " OWNERSHIP ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE identifier($ht_default_role);\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal Snowflake login or a dedicated user for Hightouch. When using the Basic engine, this user must have read access to the data you wish to sync. If using the Lightning engine, you must also grant the permissions described in the preceding snippet."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Role"
          }), " (optional) - Use this field to specify the role Hightouch should use when executing queries in Snowflake. If left blank, Hightouch uses the user's default role."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You have two options for finalizing your credentials:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Password (recommended)"
        }), "\n", _jsx(_components.li, {
          children: "RSA key pair"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The password authentication method allows for quicker setup since you only need to enter the password for the previously specified ", _jsx(_components.strong, {
          children: "username"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also supports RSA key pair authentication for environments with enhanced security requirements."
      }), "\n", _jsx(_components.h4, {
        id: "rsa-authentication",
        children: "RSA authentication"
      }), "\n", _jsxs(_components.p, {
        children: ["Generate a ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/key-pair-auth.html#step-1-generate-the-private-key",
          children: "private key"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.snowflake.com/en/user-guide/key-pair-auth.html#step-2-generate-a-public-key",
          children: "public key"
        }), " by running the following commands in your terminal:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["$ openssl genrsa 2048 | openssl pkcs8 -topk8 -inform PEM -out rsa_key.p8\n$ openssl rsa -", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " rsa_key.p8 -pubout -out rsa_key.pub\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Then in Snowflake, execute an ", _jsx(_components.code, {
          children: "ALTER USER"
        }), " command to assign the public key to your Hightouch Snowflake user:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["alter user ht_user ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "set"
          }), " rsa_public_key=", _jsx(_components.span, {
            className: "hljs-string",
            children: "'MIIBIjANBgkqh...'"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In the ", _jsx(_components.code, {
          children: "Authentication Method"
        }), " of the Snowflake Source settings, drag and drop or upload the private key you just generated into the ", _jsx(_components.strong, {
          children: "Private key file"
        }), " field."]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Snowflake"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Snowflake."]
      }), "\n", _jsx(_components.p, {
        children: "The Snowflake source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/looker-models",
            children: "Looker Looks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/sigma",
            children: "Sigma workbooks"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL editor"
        }), " allows you to query data from all databases that your Snowflake service account ", _jsx(_components.a, {
          href: "#snowflake-credential-setup",
          children: "has access to"
        }), ".\nThe ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "table selector"
        }), " only supports querying from the database specified in the ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "source configuration"
        }), "."]
      }), "\n", _jsx(SyncLogsSnippet, {
        source: "Snowflake"
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "connection-timeout",
        children: "Connection timeout"
      }), "\n", _jsxs(_components.p, {
        children: ["When initially ", _jsx(_components.a, {
          href: "#test-your-connection",
          children: "testing your connection"
        }), ", you may receive a connection timeout error.\nOnce a connection is established, subsequent API requests should happen more quickly, so it's best to retry the tests if they first fail.\nYou can do this by clicking ", _jsx(_components.strong, {
          children: "Test again"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "network-error-could-not-reach-snowflake",
        children: "Network error: Could not reach Snowflake"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive this error if the input for ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "Account identifier"
        }), " is invalid.\nInstead of using the complete Snowflake URL, for example, ", _jsx(_components.code, {
          children: "https://ACCOUNT_IDENTIFIER.snowflakecomputing.com"
        }), ",\nensure that you're only using the ", _jsx(_components.code, {
          children: "ACCOUNT_IDENTIFIER"
        }), " part of the URL, for example, ", _jsx(_components.code, {
          children: "companyname-xu12345"
        }), " or ", _jsx(_components.code, {
          children: "companyname-xu12345.us-east-1"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
